/**
 * API Service Utility
 * Handles common API operations with authentication
 */

import { getApiBaseUrl } from "./apiConfig";

class ApiService {
  constructor() {
    // Don't store baseURL - get it dynamically each time
    this.isDevelopment = process.env.NODE_ENV === "development";
  }

  // Get base URL dynamically from localStorage
  get baseURL() {
    return getApiBaseUrl();
  }

  /**
   * Get request headers
   */
  getAuthHeaders() {
    return {
      "Content-Type": "application/json",
    };
  }

  /**
   * Check if API is available
   */
  async checkApiHealth() {
    try {
      const response = await fetch(`${this.baseURL}/health`, {
        method: "GET",
        timeout: 5000,
      });

      return response.ok;
    } catch (error) {
      console.warn("API health check failed:", error);
      return false;
    }
  }

  /**
   * Make authenticated API request
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: options.method || "GET",
      headers: this.getAuthHeaders(),
      credentials: "include",
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Parse response data first
      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        // If response is not JSON, create a simple data object
        responseData = {
          message: `Request failed with status ${response.status}`,
        };
      }

      // Handle different response statuses
      if (response.status === 401) {
        // Unauthorized - session expired or invalid
        this.handleUnauthorized();
        throw new Error("Authentication failed. Please login again.");
      }

      if (response.status === 403) {
        throw new Error(
          "Access denied. You do not have permission for this action.",
        );
      }

      if (response.status === 404) {
        throw new Error("Resource not found.");
      }

      if (response.status >= 500) {
        throw new Error("Server error. Please try again later.");
      }

      if (!response.ok) {
        // Handle the specific error format from your backend
        if (responseData.success === false && responseData.errors) {
          // Backend returns validation errors in a specific format
          const errorMessage =
            responseData.errors ||
            responseData.message ||
            `Request failed with status ${response.status}`;
          throw new Error(errorMessage);
        }

        // Use the error message from the response data
        const errorMessage =
          responseData.message ||
          `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      return responseData;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  /**
   * Handle unauthorized access
   */
  handleUnauthorized() {
    // Clear stored session data
    localStorage.removeItem("token_expiry");

    // Redirect to login
    window.location.href = "/";
  }

  /**
   * Change user password
   */
  async changePassword(currentPassword, newPassword) {
    return this.makeRequest("/auth/change-password", {
      method: "PUT",
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    });
  }

  /**
   * Get user profile
   */
  async getUserProfile() {
    return this.makeRequest("/auth/profile");
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profileData) {
    return this.makeRequest("/auth/profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  /**
   * Get user notifications preferences
   */
  async getNotificationPreferences() {
    return this.makeRequest("/user/notifications/preferences");
  }

  /**
   * Update user notifications preferences
   */
  async updateNotificationPreferences(preferences) {
    return this.makeRequest("/user/notifications/preferences", {
      method: "PUT",
      body: JSON.stringify(preferences),
    });
  }

  /**
   * Create withdrawal request
   */
  async createWithdrawal(withdrawalData) {
    return this.makeRequest("/withdrawal", {
      method: "POST",
      body: JSON.stringify(withdrawalData),
    });
  }

  /**
   * Get merchant static wallet settings
   */
  async getMerchantStaticWallet() {
    return this.makeRequest("/merchant/static-wallet", {
      method: "GET",
    });
  }

  /**
   * Update merchant static wallet settings
   */
  async updateMerchantStaticWallet(walletData) {
    return this.makeRequest("/merchant/static-wallet", {
      method: "PUT",
      body: JSON.stringify(walletData),
    });
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
